/**
 * A React component that handles OTP verification.
 * @returns The OTP verification component.
 */
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { forgotAdmin, verifyAdminOtp } from "../../../store/features/auth/authSlice";
import { clubListResponce } from "../../../store/features/club/clubSlice";
import {
  currentEventListResponce,
  pastEventListResponce,
} from "../../../store/features/event/eventSlice";
import { showToast } from "../../../store/features/toast/toastSlice";
import Constant from "../../../util/constant";
import OtpVerificationComponnet from "./otpVerificationComponent";
// actions

/**
 * A container component for OTP verification.
 * @param {Object} props - The component props.
 * @returns The rendered OTP verification component.
 */
const OtpVerificationContainer = (props) => {
  /**
   * Navigates to a new page using the useNavigate hook.
   * @param {function} useNavigate - The useNavigate hook from React Router.
   * @returns None
   */
  const navigate = useNavigate();
  const email = useSelector(
    (state) => state?.auth?.forgotResponce?.data?.email
  );
  const emailFallback = window.sessionStorage.getItem(
    Constant.SESSIONSTORAGEKEYS.FORGOT_EMAIL
  );
  const resolvedEmail = email || emailFallback || "";
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(false);
  const [digitValue, setDigitValue] = useState("");
  const [timer, setTimer] = useState(30);
  const [otpErrorCode, setOtpErrorCode] = useState("");
  const [otpErrorMessage, setOtpErrorMessage] = useState("");

  useEffect(() => {
    localStorage.clear();
  }, []);

  /**
   * Updates the digit value when the OTP (One-Time Password) input changes.
   * @param {{string}} value - The new value of the OTP input.
   * @returns None
   */

  const otpContainer = document?.querySelector(".otpContainer");
  const lastInput = otpContainer?.querySelector(
    'input[aria-label^="Please enter OTP character"]:last-child'
  );
  if (lastInput) {
    if (lastInput.value.trim() !== "") {
      lastInput.blur();
    }
  }
  const onOtpChange = (value) => {
    setDigitValue(value);
    setOtpErrorCode("");
    setOtpErrorMessage("");
  };
  /**
   * Handles the form submission event.
   * @param {any} value - The value submitted from the form.
   * @returns None
   */
  const onFormSubmit = async (value) => {
    setBusy(true);
    const otpRequestId = window.sessionStorage.getItem(
      Constant.SESSIONSTORAGEKEYS.OTP_REQUEST_ID
    );
    dispatch(verifyAdminOtp({ otp: digitValue, otpRequestId })).then((resultAction) => {
      if (verifyAdminOtp.fulfilled.match(resultAction)) {
      dispatch(
        showToast({
          show: false,
            message: resultAction?.payload?.message || t("OTP_VERIFIED_UNLOCKED"),
          type: "success",
        })
      );
      window.sessionStorage.setItem(Constant.SESSIONSTORAGEKEYS.RESENDOTP, "");
        window.sessionStorage.setItem(
          Constant.SESSIONSTORAGEKEYS.EVENTS_OTP_VERIFIED,
          "true"
        );
        dispatch(currentEventListResponce({}));
        dispatch(pastEventListResponce({}));
        dispatch(clubListResponce());
        setOtpErrorCode("");
        setOtpErrorMessage("");
      navigate("/reset-password");
      } else {
        const code = resultAction?.payload?.messageCode || resultAction?.payload?.message || "";
        const fallback = resultAction?.payload?.message || t("INCORRECT_CODE");
        setOtpErrorCode(code);
        setOtpErrorMessage(
          code === "OTP_EXPIRED" ? t("OTP_EXPIRED_MSG") : code === "OTP_INVALID" ? t("OTP_INVALID_MSG") : fallback
        );
      }
      setBusy(false);
    });
  };
  /**
   * Handles the click event for the resend button in the forget password flow.
   * Sends a request to the server to resend the OTP to the user's email.
   * Updates the timer, session storage, and displays a success toast message if successful.
   * Displays an error toast message if an error occurs.
   * @returns None
   */
  const handleResendClick = async () => {
    dispatch(forgotAdmin(resolvedEmail)).then((resultAction) => {
      if (forgotAdmin.fulfilled.match(resultAction)) {
        setTimer(30);
        setDigitValue("");
        window.sessionStorage.setItem(
          Constant.SESSIONSTORAGEKEYS.RESENDOTP,
          resultAction?.payload?.data?.otp
        );
        window.sessionStorage.setItem(
          Constant.SESSIONSTORAGEKEYS.OTP_REQUEST_ID,
          resultAction?.payload?.data?.otpRequestId || ""
        );
        window.sessionStorage.setItem(Constant.SESSIONSTORAGEKEYS.OTP, "");
        dispatch(
          showToast({
            show: false,
            message: resultAction?.payload?.message,
            type: "success",
          })
        );
      } else if (forgotAdmin.rejected.match(resultAction)) {
        setBusy(false);
        setDigitValue("");
        dispatch(
          showToast({
            show: false,
            message: resultAction?.error?.message,
            type: "error",
          })
        );
      }
    });
  };
  /**
   * useEffect hook that starts a countdown timer and clears the timer when it reaches 0.
   * @param {number} timer - The initial value of the timer.
   * @returns None
   */
  useEffect(() => {
    if (timer > 0) {
      const timerID = setTimeout(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);

      return () => {
        clearTimeout(timerID);
      };
    }
  }, [timer]);
  /**
   * An object representing an OTP (One-Time Password) form.
   * @property {object} otp - The OTP input field configuration.
   * @property {string} otp.name - The name of the OTP input field.
   * @property {object} otp.validate - The validation rules for the OTP input field.
   * @property {object} otp.validate.required - The required validation rule for the OTP input field.
   * @property {boolean} otp.validate.required.value - The value indicating whether the OTP input field is required.
   */
  const otpForm = {
    otp: {
      name: "otp",
      validate: {
        required: {
          value: true,
        },
      },
    },
  };

  return (
    <OtpVerificationComponnet
      busy={busy}
      onFormSubmit={onFormSubmit}
      digitValue={digitValue}
      onOtpChange={onOtpChange}
      timer={timer}
      handleResendClick={handleResendClick}
      otpForm={otpForm}
      otpErrorCode={otpErrorCode}
      otpErrorMessage={otpErrorMessage}
      email={resolvedEmail}
    />
  );
};

export default OtpVerificationContainer;
